import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { faqList } from "@/mocks/home";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      mainEntity: faqList.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://bhmbk.cn/" },
        { "@type": "ListItem", position: 2, name: "常见问题", item: "https://bhmbk.cn/faq" },
      ],
    },
  ],
};

export default function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-foreground-950">
              常见问题
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              关于内容范围、更新机制、搜索方式和信息核对，这里有集中的说明。如果还有疑问，可在页面底部联系我们。
            </p>
          </div>
        </section>

        <section className="w-full">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-4">
                <div className="sticky top-24 p-5 rounded-lg bg-background-100 border border-background-200">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">快速导航</h2>
                  <ul className="mt-4 space-y-3">
                    {faqList.map((f, i) => (
                      <li key={f.question}>
                        <button
                          onClick={() => setOpen(i)}
                          className={`text-left text-sm transition-colors ${
                            open === i ? "text-primary-600" : "text-foreground-600 hover:text-foreground-900"
                          }`}
                        >
                          {f.question}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/about#contact"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-chat-3-line"></i>
                    还有问题要问
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="rounded-lg border border-background-200 bg-background-100 divide-y divide-background-200 overflow-hidden">
                  {faqList.map((f, i) => {
                    const isOpen = open === i;
                    return (
                      <div key={f.question} id={`faq-${i}`}>
                        <button
                          onClick={() => setOpen(isOpen ? null : i)}
                          aria-expanded={isOpen}
                          className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-5 hover:bg-background-200/50 transition-colors"
                        >
                          <span className="font-heading text-base md:text-lg font-bold text-foreground-900">
                            {f.question}
                          </span>
                          <span
                            className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-full transition-colors ${
                              isOpen ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-600"
                            }`}
                          >
                            <i className={isOpen ? "ri-subtract-line" : "ri-add-line"}></i>
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-5 md:px-6 pb-6 -mt-1">
                            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{f.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 p-6 rounded-lg bg-accent-50 border border-accent-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-950">
                    没找到答案？
                  </h3>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                    把你遇到的问题和具体的合集名称告诉我们，我们会人工核对后回复。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-600 transition-colors"
                  >
                    <i className="ri-mail-send-line"></i>
                    前往联系表单
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}